import React, { useState, useEffect } from 'react';
import { 
  fetchTreasurySummary, 
  fetchTreasuryTransactions, 
  addTreasuryTransaction, 
  fetchDebts, 
  addDebt, 
  addDebtPayment,
  updateTreasuryTransaction,
  deleteTreasuryTransaction,
  updateDebt,
  deleteDebt
} from '../utils/treasury_api';
import { getEmployees, getCurrentRate, formatIQD, formatUSD } from '../utils/api';
import { parseArabicToEnglish } from '../utils/formatters';
import { Plus, Wallet, ArrowDownRight, ArrowUpRight, CheckCircle, HandCoins, Users, Edit, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function TreasuryPage({ defaultTab = 'treasury' }) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [viewCurrency, setViewCurrency] = useState('IQD'); // Default to Dinar (user request)
  const [exchangeRate, setExchangeRate] = useState(1500);
  const [loading, setLoading] = useState(true);
  
  // Logged-in user role check
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin';

  // Treasury Data
  const [summary, setSummary] = useState({ 
    currentBalance: { USD: 0, IQD: 0 }, 
    cashIn: { USD: 0, IQD: 0 }, 
    cashOut: { USD: 0, IQD: 0 }, 
    totalDebtsGiven: { USD: 0, IQD: 0 } 
  });
  const [transactions, setTransactions] = useState([]);
  
  // Debts Data
  const [debts, setDebts] = useState([]);
  const [employeesList, setEmployeesList] = useState([]);
  const [isExternalDebt, setIsExternalDebt] = useState(true);

  // Modals
  const [showTxModal, setShowTxModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  const [selectedTx, setSelectedTx] = useState(null);
  const [selectedDebt, setSelectedDebt] = useState(null);
  
  // Edit Debt Modals State
  const [showEditDebtModal, setShowEditDebtModal] = useState(false);
  const [editingDebt, setEditingDebt] = useState(null);
  const [isExternalEditDebt, setIsExternalEditDebt] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Fetch current exchange rate dynamically
      try {
        const rateRes = await getCurrentRate();
        if (rateRes && rateRes.usd_to_iqd) {
          setExchangeRate(parseFloat(rateRes.usd_to_iqd));
        }
      } catch (err) {
        console.warn('Could not fetch exchange rate, defaulting to 1500', err);
      }

      // 2. Fetch specific tab data
      if (activeTab === 'treasury') {
        const sumRes = await fetchTreasurySummary();
        if (sumRes.success) setSummary(sumRes.data);
        
        const txRes = await fetchTreasuryTransactions();
        if (txRes.success) setTransactions(txRes.data);
      } else {
        const dRes = await fetchDebts();
        if (dRes.success) setDebts(dRes.data);
        
        const empRes = await getEmployees();
        if (Array.isArray(empRes)) setEmployeesList(empRes);
      }
    } catch (err) {
      console.error(err);
      toast.error('حدث خطأ أثناء جلب البيانات');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const handleAddTx = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = {
      type: fd.get('type'),
      amount: parseFloat(fd.get('amount')),
      currency: fd.get('currency'),
      description: fd.get('description')
    };

    const res = await addTreasuryTransaction(data);
    if (res.success) {
      toast.success(res.message);
      setShowTxModal(false);
      loadData();
    } else {
      toast.error(res.message);
    }
  };

  const handleEditTxSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = {
      type: fd.get('type'),
      amount: parseFloat(fd.get('amount')),
      currency: fd.get('currency'),
      description: fd.get('description')
    };

    const res = await updateTreasuryTransaction(selectedTx.id, data);
    if (res.success) {
      toast.success(res.message);
      setShowEditModal(false);
      setSelectedTx(null);
      loadData();
    } else {
      toast.error(res.message);
    }
  };

  const handleDeleteTx = async (tx) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الحركة؟ سيتم التراجع عن كافة التأثيرات المالية المرتبطة بها.')) {
      try {
        const res = await deleteTreasuryTransaction(tx.id);
        if (res.success) {
          toast.success(res.message);
          loadData();
        } else {
          toast.error(res.message);
        }
      } catch (err) {
        console.error(err);
        toast.error('حدث خطأ أثناء حذف الحركة');
      }
    }
  };

  const handleAddDebt = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const empId = fd.get('employee_id');
    let pName = fd.get('person_name');
    
    if (empId) {
      const emp = employeesList.find(emp => emp.id.toString() === empId);
      if (emp) pName = emp.name;
    }

    const data = {
      person_name: pName,
      employee_id: empId || null,
      amount: parseFloat(fd.get('amount')),
      currency: fd.get('currency'),
      debt_type: fd.get('debt_type'),
      notes: fd.get('notes')
    };

    const res = await addDebt(data);
    if (res.success) {
      toast.success(res.message);
      setShowDebtModal(false);
      loadData();
    } else {
      toast.error(res.message);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = {
      amount_paid: parseFloat(fd.get('amount_paid')),
      notes: fd.get('notes')
    };

    const res = await addDebtPayment(selectedDebt.id, data);
    if (res.success) {
      toast.success(res.message);
      setShowPaymentModal(false);
      setSelectedDebt(null);
      loadData();
    } else {
      toast.error(res.message);
    }
  };

  const handleEditDebtSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const empId = fd.get('employee_id');
    let pName = fd.get('person_name');
    
    if (empId) {
      const emp = employeesList.find(emp => emp.id.toString() === empId);
      if (emp) pName = emp.name;
    }

    const data = {
      person_name: pName,
      employee_id: empId || null,
      amount: parseFloat(fd.get('amount')),
      currency: fd.get('currency'),
      debt_type: fd.get('debt_type'),
      notes: fd.get('notes'),
      status: fd.get('status')
    };

    const res = await updateDebt(editingDebt.id, data);
    if (res.success) {
      toast.success(res.message);
      setShowEditDebtModal(false);
      setEditingDebt(null);
      loadData();
    } else {
      toast.error(res.message);
    }
  };

  const handleDeleteDebt = async (debt) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الدين بالكامل؟ سيتم حذف جميع سندات القبض (الدفعات) والعمليات المالية المرتبطة به.')) {
      try {
        const res = await deleteDebt(debt.id);
        if (res.success) {
          toast.success(res.message);
          loadData();
        } else {
          toast.error(res.message);
        }
      } catch (err) {
        console.error(err);
        toast.error('حدث خطأ أثناء حذف الدين');
      }
    }
  };

  // Convert amounts dynamically on the fly to support perfect single physical pool reflection
  const getConvertedAmount = (amount, fromCurrency, targetCurrency) => {
    const amt = parseFloat(amount || 0);
    if (fromCurrency === targetCurrency) return amt;
    if (fromCurrency === 'USD' && targetCurrency === 'IQD') return amt * exchangeRate;
    if (fromCurrency === 'IQD' && targetCurrency === 'USD') return amt / exchangeRate;
    return amt;
  };

  const formatCurrency = (amount, currency = 'IQD') => {
    if (currency === 'IQD') {
      return formatIQD(amount);
    }
    return formatUSD(amount);
  };

  return (
    <div className="space-y-6">
      {/* Tab bar header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        {/* Navigation Tabs */}
        <div className="flex bg-[var(--bg-flat-50)] p-1 rounded-xl border border-[var(--border-light)] shadow-sm">
          <button 
            onClick={() => setActiveTab('treasury')}
            className={`px-5 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'treasury' ? 'bg-white shadow-sm text-primary' : 'text-[var(--text-muted)] hover:text-[var(--text-dark)]'}`}
          >
            حركات الخزينة والملخص
          </button>
          <button 
            onClick={() => setActiveTab('debts')}
            className={`px-5 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'debts' ? 'bg-white shadow-sm text-primary' : 'text-[var(--text-muted)] hover:text-[var(--text-dark)]'}`}
          >
            سجل الديون والسلف بالسوق
          </button>
        </div>

        {/* Currency & Actions bar */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Currency Toggle */}
          <div className="flex bg-[var(--bg-flat-50)] p-1 rounded-xl border border-[var(--border-light)] shadow-sm">
            <button 
              onClick={() => setViewCurrency('IQD')}
              className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${viewCurrency === 'IQD' ? 'bg-white shadow-sm text-green-600' : 'text-[var(--text-muted)] hover:text-[var(--text-dark)]'}`}
            >
              دينار عراقي (IQD)
            </button>
            <button 
              onClick={() => setViewCurrency('USD')}
              className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${viewCurrency === 'USD' ? 'bg-white shadow-sm text-green-600' : 'text-[var(--text-muted)] hover:text-[var(--text-dark)]'}`}
            >
              دولار أمريكي (USD)
            </button>
          </div>

          {activeTab === 'treasury' ? (
            <button onClick={() => setShowTxModal(true)} className="btn btn-primary flex items-center gap-2">
              <Plus size={18} /> حركة جديدة (إيداع / سحب)
            </button>
          ) : (
            <button onClick={() => setShowDebtModal(true)} className="btn btn-primary flex items-center gap-2">
              <Plus size={18} /> إضافة سلفة / دين جديد
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[var(--text-muted)]">جاري التحميل...</div>
      ) : activeTab === 'treasury' ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-[var(--bg-dark-card)] border border-[var(--border-light)] p-5 rounded-2xl flex flex-col justify-center shadow-sm">
              <div className="flex items-center gap-3 text-[var(--text-muted)] mb-2">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg dark:bg-blue-900/30 dark:text-blue-400">
                  <Wallet size={20} />
                </div>
                <span className="font-semibold text-sm">الرصيد الفعلي (الكاش)</span>
              </div>
              <p className="text-2xl font-bold text-[var(--text-dark-h)] mt-2" dir="ltr">
                {formatCurrency(summary.currentBalance?.[viewCurrency] || 0, viewCurrency)}
              </p>
            </div>
            
            <div className="bg-white dark:bg-[var(--bg-dark-card)] border border-[var(--border-light)] p-5 rounded-2xl flex flex-col justify-center shadow-sm">
              <div className="flex items-center gap-3 text-[var(--text-muted)] mb-2">
                <div className="p-2 bg-green-100 text-green-600 rounded-lg dark:bg-green-900/30 dark:text-green-400">
                  <ArrowDownRight size={20} />
                </div>
                <span className="font-semibold text-sm">إجمالي الداخل</span>
              </div>
              <p className="text-2xl font-bold text-green-600 mt-2" dir="ltr">
                +{formatCurrency(summary.cashIn?.[viewCurrency] || 0, viewCurrency)}
              </p>
            </div>

            <div className="bg-white dark:bg-[var(--bg-dark-card)] border border-[var(--border-light)] p-5 rounded-2xl flex flex-col justify-center shadow-sm">
              <div className="flex items-center gap-3 text-[var(--text-muted)] mb-2">
                <div className="p-2 bg-red-100 text-red-600 rounded-lg dark:bg-red-900/30 dark:text-red-400">
                  <ArrowUpRight size={20} />
                </div>
                <span className="font-semibold text-sm">إجمالي الخارج</span>
              </div>
              <p className="text-2xl font-bold text-red-500 mt-2" dir="ltr">
                -{formatCurrency(summary.cashOut?.[viewCurrency] || 0, viewCurrency)}
              </p>
            </div>

            <div className="bg-white dark:bg-[var(--bg-dark-card)] border border-[var(--border-light)] p-5 rounded-2xl flex flex-col justify-center shadow-sm">
              <div className="flex items-center gap-3 text-[var(--text-muted)] mb-2">
                <div className="p-2 bg-orange-100 text-orange-600 rounded-lg dark:bg-orange-900/30 dark:text-orange-400">
                  <HandCoins size={20} />
                </div>
                <span className="font-semibold text-sm">ديون وسلف بالسوق</span>
              </div>
              <p className="text-2xl font-bold text-[var(--text-dark-h)] mt-2" dir="ltr">
                {formatCurrency(summary.totalDebtsGiven?.[viewCurrency] || 0, viewCurrency)}
              </p>
            </div>
          </div>

          {/* Transactions List */}
          <div className="bg-white dark:bg-[var(--bg-dark-card)] border border-[var(--border-light)] rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-sm text-right">
              <thead className="bg-[var(--bg-flat-50)] text-[var(--text-muted)]">
                <tr>
                  <th className="p-4 font-semibold">التاريخ</th>
                  <th className="p-4 font-semibold">النوع</th>
                  <th className="p-4 font-semibold">البيان</th>
                  <th className="p-4 font-semibold">المبلغ</th>
                  {isOwnerOrAdmin && <th className="p-4 font-semibold text-center">الإجراءات</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-light)] text-[var(--text-dark)]">
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={isOwnerOrAdmin ? 5 : 4} className="p-6 text-center text-[var(--text-muted)]">
                      لا توجد حركات مسجلة بالخزينة
                    </td>
                  </tr>
                ) : transactions.map(tx => {
                  const originalCurrency = tx.currency || 'IQD';
                  const convertedAmt = getConvertedAmount(tx.amount, originalCurrency, viewCurrency);
                  const isIncome = ['income', 'investment', 'debt_settlement'].includes(tx.type);

                  return (
                    <tr key={`${tx.source}-${tx.id}`} className="hover:bg-[var(--bg-flat-50)] transition-colors">
                      <td className="p-4" dir="ltr">
                        {tx.created_at ? new Date(tx.created_at.toString().replace(' ', 'T')).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                      </td>
                      <td className="p-4">
                        {tx.type === 'income' && <span className="px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-bold dark:bg-green-900/30 dark:text-green-400">إيرادات</span>}
                        {tx.type === 'investment' && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-bold dark:bg-blue-900/30 dark:text-blue-400">ضخ استثمار</span>}
                        {tx.type === 'debt_settlement' && <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-md text-xs font-bold dark:bg-emerald-900/30 dark:text-emerald-400">تسديد سلفة/دين</span>}
                        {tx.type === 'expense' && <span className="px-2 py-1 bg-red-100 text-red-700 rounded-md text-xs font-bold dark:bg-red-900/30 dark:text-red-400">مصروفات</span>}
                        {tx.type === 'profit_withdrawal' && <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-md text-xs font-bold dark:bg-orange-900/30 dark:text-orange-400">سحب أرباح</span>}
                      </td>
                      <td className="p-4 max-w-[280px] truncate" title={tx.description}>{tx.description}</td>
                      <td className="p-4 font-bold" dir="ltr">
                        {isIncome ? (
                          <span className="text-green-600">+{formatCurrency(convertedAmt, viewCurrency)}</span>
                        ) : (
                          <span className="text-red-500">-{formatCurrency(convertedAmt, viewCurrency)}</span>
                        )}
                        {originalCurrency !== viewCurrency && (
                          <div className="text-[10px] text-gray-400 font-normal mt-0.5">
                            ({formatCurrency(tx.amount, originalCurrency)})
                          </div>
                        )}
                      </td>
                      {isOwnerOrAdmin && (
                        <td className="p-4 text-center">
                          {tx.source === 'treasury' ? (
                            <div className="flex justify-center gap-2">
                              <button 
                                onClick={() => { setSelectedTx(tx); setShowEditModal(true); }}
                                className="text-blue-600 hover:text-blue-800 p-1.5 rounded hover:bg-blue-50 transition-colors"
                                title="تعديل الحركة"
                              >
                                <Edit size={15} />
                              </button>
                              <button 
                                onClick={() => handleDeleteTx(tx)}
                                className="text-red-600 hover:text-red-800 p-1.5 rounded hover:bg-red-50 transition-colors"
                                title="حذف الحركة"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          ) : (
                            <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full font-medium">تلقائي</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Debts List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {debts.length === 0 ? (
              <div className="col-span-full text-center py-10 text-[var(--text-muted)]">لا توجد ديون أو سلف مسجلة</div>
            ) : debts.map(debt => {
              const originalCurrency = debt.currency || 'IQD';
              const convertedAmount = getConvertedAmount(debt.amount, originalCurrency, viewCurrency);
              const convertedRemaining = getConvertedAmount(debt.remaining, originalCurrency, viewCurrency);

              return (
                <div key={debt.id} className="bg-white dark:bg-[var(--bg-dark-card)] border border-[var(--border-light)] p-5 rounded-2xl shadow-sm flex flex-col gap-4">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-[var(--bg-flat-50)] rounded-full">
                        <Users className="text-primary" size={20} />
                      </div>
                      <div>
                        <h3 className="font-bold text-[var(--text-dark-h)]">{debt.person_name}</h3>
                        <p className="text-xs text-[var(--text-muted)] mt-1">
                          {debt.debt_type === 'cash_advance' ? 'سلفة نقدية' : debt.debt_type === 'subscription_fee' ? 'دفع اشتراك' : 'أخرى'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isOwnerOrAdmin && (
                        <div className="flex items-center gap-1 mr-2">
                          <button
                            onClick={() => {
                              setEditingDebt(debt);
                              setIsExternalEditDebt(!debt.employee_id);
                              setShowEditDebtModal(true);
                            }}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                            title="تعديل الدين"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteDebt(debt)}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="حذف الدين"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                      {debt.status === 'settled' ? (
                        <span className="flex items-center gap-1 text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded-md">
                          <CheckCircle size={14} /> مسدد
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs font-bold text-orange-600 bg-orange-100 px-2 py-1 rounded-md">
                          قيد السداد
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm bg-[var(--bg-flat-50)] p-3 rounded-xl border border-[var(--border-light)]">
                    <div>
                      <p className="text-[var(--text-muted)] text-xs mb-1">المبلغ الأساسي</p>
                      <p className="font-bold text-[var(--text-dark)]" dir="ltr">{formatCurrency(convertedAmount, viewCurrency)}</p>
                      {originalCurrency !== viewCurrency && (
                        <p className="text-[10px] text-gray-400 mt-0.5" dir="ltr">({formatCurrency(debt.amount, originalCurrency)})</p>
                      )}
                    </div>
                    <div>
                      <p className="text-[var(--text-muted)] text-xs mb-1">المتبقي</p>
                      <p className="font-bold text-red-500" dir="ltr">{formatCurrency(convertedRemaining, viewCurrency)}</p>
                      {originalCurrency !== viewCurrency && (
                        <p className="text-[10px] text-gray-400 mt-0.5" dir="ltr">({formatCurrency(debt.remaining, originalCurrency)})</p>
                      )}
                    </div>
                  </div>

                  {debt.notes && (
                    <p className="text-xs text-[var(--text-muted)] leading-relaxed bg-[var(--bg-flat-50)] p-2.5 rounded-lg italic border border-[var(--border-light)]">
                      {debt.notes}
                    </p>
                  )}

                  {debt.status !== 'settled' && (
                    <button 
                      onClick={() => { setSelectedDebt(debt); setShowPaymentModal(true); }}
                      className="mt-auto w-full btn border border-[var(--border-light)] bg-white hover:bg-green-50 hover:text-green-700 hover:border-green-200 text-[var(--text-dark)] font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      <HandCoins size={18} /> سند قبض (استلام دفعة)
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* --- MODALS --- */}
      
      {/* 1. Treasury Tx Modal (Add) */}
      {showTxModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[var(--bg-dark-card)] w-full max-w-md rounded-2xl shadow-xl overflow-hidden border border-[var(--border-light)]">
            <div className="p-6 border-b border-[var(--border-light)] flex justify-between items-center">
              <h2 className="text-xl font-bold text-[var(--text-dark-h)]">حركة خزينة جديدة</h2>
            </div>
            <form onSubmit={handleAddTx} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">النوع</label>
                <select name="type" className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required>
                  <option value="">اختر النوع...</option>
                  <option value="investment">إيداع: ضخ استثمار (رأس مال)</option>
                  <option value="income">إيداع: إيرادات أخرى</option>
                  <option value="profit_withdrawal">سحب: توزيع أرباح</option>
                  <option value="expense">سحب: مصاريف نقدية أخرى</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">العملة</label>
                  <select name="currency" className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required>
                    <option value="IQD">دينار (ع.د)</option>
                    <option value="USD">دولار ($)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">المبلغ</label>
                  <input type="text" inputMode="decimal" name="amount" onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">البيان / ملاحظات</label>
                <textarea name="description" className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" rows="2" placeholder="تفاصيل الحركة..." required></textarea>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn btn-primary flex-1 py-3 text-sm">حفظ الحركة</button>
                <button type="button" onClick={() => setShowTxModal(false)} className="btn bg-[var(--bg-flat-50)] hover:bg-[var(--border-light)] text-[var(--text-dark)] flex-1 py-3 text-sm border border-[var(--border-light)]">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. Treasury Edit Tx Modal */}
      {showEditModal && selectedTx && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[var(--bg-dark-card)] w-full max-w-md rounded-2xl shadow-xl overflow-hidden border border-[var(--border-light)]">
            <div className="p-6 border-b border-[var(--border-light)] flex justify-between items-center">
              <h2 className="text-xl font-bold text-[var(--text-dark-h)]">تعديل الحركة المالية</h2>
            </div>
            <form onSubmit={handleEditTxSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">النوع</label>
                <select name="type" defaultValue={selectedTx.type} className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required>
                  <option value="investment">إيداع: ضخ استثمار (رأس مال)</option>
                  <option value="income">إيداع: إيرادات أخرى</option>
                  <option value="profit_withdrawal">سحب: توزيع أرباح</option>
                  <option value="expense">سحب: مصاريف نقدية أخرى</option>
                  <option value="debt_settlement">تسديد سلفة/دين</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">العملة</label>
                  <select name="currency" defaultValue={selectedTx.currency || 'IQD'} className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required>
                    <option value="IQD">دينار (ع.د)</option>
                    <option value="USD">دولار ($)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">المبلغ</label>
                  <input type="text" inputMode="decimal" name="amount" defaultValue={selectedTx.amount} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">البيان / ملاحظات</label>
                <textarea name="description" defaultValue={selectedTx.description} className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" rows="2" required></textarea>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn btn-primary flex-1 py-3 text-sm">حفظ التعديلات</button>
                <button type="button" onClick={() => { setShowEditModal(false); setSelectedTx(null); }} className="btn bg-[var(--bg-flat-50)] hover:bg-[var(--border-light)] text-[var(--text-dark)] flex-1 py-3 text-sm border border-[var(--border-light)]">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. Add Debt Modal */}
      {showDebtModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[var(--bg-dark-card)] w-full max-w-md rounded-2xl shadow-xl overflow-hidden border border-[var(--border-light)]">
            <div className="p-6 border-b border-[var(--border-light)] flex justify-between items-center">
              <h2 className="text-xl font-bold text-[var(--text-dark-h)]">إضافة سلفة أو دين جديد</h2>
            </div>
            <form onSubmit={handleAddDebt} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">الموظف / Team Leader</label>
                <select 
                  name="employee_id" 
                  className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]"
                  onChange={(e) => setIsExternalDebt(!e.target.value)}
                >
                  <option value="">-- شخص خارجي (كتابة الاسم يدوياً) --</option>
                  {employeesList.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name} ({emp.role})</option>
                  ))}
                </select>
              </div>
              {isExternalDebt && (
                <div>
                  <label className="block text-sm font-semibold mb-2">الاسم (للأشخاص الخارجيين)</label>
                  <input type="text" name="person_name" className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required={isExternalDebt} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">العملة</label>
                  <select name="currency" className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required>
                    <option value="IQD">دينار (ع.د)</option>
                    <option value="USD">دولار ($)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">المبلغ</label>
                  <input type="text" inputMode="decimal" name="amount" onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">النوع</label>
                <select name="debt_type" className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]">
                  <option value="cash_advance">سلفة نقدية</option>
                  <option value="subscription_fee">دفع رسوم اشتراك (مثل Adobe)</option>
                  <option value="other">أخرى</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">ملاحظات</label>
                <textarea name="notes" className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" rows="2"></textarea>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn btn-primary flex-1 py-3 text-sm">حفظ الدين</button>
                <button type="button" onClick={() => setShowDebtModal(false)} className="btn bg-[var(--bg-flat-50)] hover:bg-[var(--border-light)] text-[var(--text-dark)] flex-1 py-3 text-sm border border-[var(--border-light)]">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. Add Payment Modal */}
      {showPaymentModal && selectedDebt && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[var(--bg-dark-card)] w-full max-w-md rounded-2xl shadow-xl overflow-hidden border border-[var(--border-light)]">
            <div className="p-6 border-b border-[var(--border-light)] flex justify-between items-center">
              <h2 className="text-xl font-bold text-[var(--text-dark-h)]">إنشاء سند قبض</h2>
            </div>
            <div className="px-6 py-4 bg-orange-50 border-b border-orange-100">
              <p className="text-sm text-orange-800">
                تسديد جزء (أو كل) الدين الخاص بـ <strong>{selectedDebt.person_name}</strong>.
                <br/> المتبقي: <strong>{formatCurrency(selectedDebt.remaining, selectedDebt.currency)}</strong>
              </p>
            </div>
            <form onSubmit={handleAddPayment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">المبلغ المسدد ({selectedDebt.currency === 'USD' ? '$' : 'ع.د'})</label>
                <input type="text" inputMode="decimal" name="amount_paid" onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">ملاحظات (اختياري)</label>
                <input type="text" name="notes" placeholder="مثال: خصم من الراتب، أو تحويل كاش" className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn btn-primary flex-1 py-3 text-sm bg-green-600 hover:bg-green-700 shadow-lg shadow-green-500/30 border-transparent text-white">حفظ سند القبض</button>
                <button type="button" onClick={() => { setShowPaymentModal(false); setSelectedDebt(null); }} className="btn bg-[var(--bg-flat-50)] hover:bg-[var(--border-light)] text-[var(--text-dark)] flex-1 py-3 text-sm border border-[var(--border-light)]">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. Edit Debt Modal */}
      {showEditDebtModal && editingDebt && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[var(--bg-dark-card)] w-full max-w-md rounded-2xl shadow-xl overflow-hidden border border-[var(--border-light)]">
            <div className="p-6 border-b border-[var(--border-light)] flex justify-between items-center">
              <h2 className="text-xl font-bold text-[var(--text-dark-h)]">تعديل السلفة أو الدين</h2>
            </div>
            <form onSubmit={handleEditDebtSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">الموظف / Team Leader</label>
                <select 
                  name="employee_id" 
                  defaultValue={editingDebt.employee_id || ''}
                  className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]"
                  onChange={(e) => setIsExternalEditDebt(!e.target.value)}
                >
                  <option value="">-- شخص خارجي (كتابة الاسم يدوياً) --</option>
                  {employeesList.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name} ({emp.role})</option>
                  ))}
                </select>
              </div>
              {isExternalEditDebt && (
                <div>
                  <label className="block text-sm font-semibold mb-2">الاسم (للأشخاص الخارجيين)</label>
                  <input 
                    type="text" 
                    name="person_name" 
                    defaultValue={editingDebt.person_name}
                    className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" 
                    required={isExternalEditDebt} 
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">العملة</label>
                  <select name="currency" defaultValue={editingDebt.currency || 'IQD'} className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" required>
                    <option value="IQD">دينار (ع.د)</option>
                    <option value="USD">دولار ($)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">المبلغ</label>
                  <input 
                    type="text" 
                    inputMode="decimal" 
                    name="amount" 
                    defaultValue={editingDebt.amount}
                    onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} 
                    className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" 
                    required 
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">النوع</label>
                  <select name="debt_type" defaultValue={editingDebt.debt_type || 'other'} className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]">
                    <option value="cash_advance">سلفة نقدية</option>
                    <option value="subscription_fee">دفع رسوم اشتراك</option>
                    <option value="other">أخرى</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">الحالة</label>
                  <select name="status" defaultValue={editingDebt.status || 'active'} className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]">
                    <option value="active">قيد السداد</option>
                    <option value="settled">مسدد</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">ملاحظات</label>
                <textarea 
                  name="notes" 
                  defaultValue={editingDebt.notes}
                  className="w-full p-3 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)]" 
                  rows="2"
                ></textarea>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn btn-primary flex-1 py-3 text-sm">حفظ التغييرات</button>
                <button type="button" onClick={() => { setShowEditDebtModal(false); setEditingDebt(null); }} className="btn bg-[var(--bg-flat-50)] hover:bg-[var(--border-light)] text-[var(--text-dark)] flex-1 py-3 text-sm border border-[var(--border-light)]">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
